import { useCallback } from "react";
import useTranslation from "next-translate/useTranslation";
import * as gtag from "@lib/utilities/analytics";

/**
 * The phone CTA — the site's primary conversion (items 1+6+13).
 *
 * One component for every phone affordance so the GA4 event cannot be
 * forgotten on one of them: `phone_click` fires on every tap/click, with the
 * placement as a label, and mobile vs desktop is segmented in GA by device
 * category rather than by us guessing from the viewport.
 *
 * The number is intentionally hardcoded as a fallback but overridable — call
 * tracking (dynamic number insertion) will swap it at runtime, which is why it
 * renders from a prop rather than being baked into copy strings anywhere else.
 */
const PHONE_DISPLAY = "210 4972 088";
const PHONE_TEL = "+302104972088";

const ICON = (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
       strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L8.1 9.5a16 16 0 0 0 6 6l1.1-1.1a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2Z" />
  </svg>
);

export interface PhoneLinkProps {
  variant?: "primary" | "inverse" | "header" | "footer" | "floating" | "plain" | "card";
  placement?: string;
  display?: string;
  tel?: string;
  showNumber?: boolean;
}

const STYLES: Record<NonNullable<PhoneLinkProps["variant"]>, string> = {
  primary:
    "inline-flex items-center gap-2 rounded-full bg-violet-700 px-6 py-3 font-extrabold text-white transition-colors hover:bg-violet-800",
  // white pill on a violet ground — the artifact's m-ctabtn
  inverse:
    "inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-extrabold text-violet-700 transition-colors hover:bg-violet-050",
  header:
    "inline-flex items-center gap-2 rounded-full bg-violet-700 px-4 py-2 text-sm font-extrabold text-white transition-colors hover:bg-violet-800",
  footer: "inline-flex items-center gap-2 font-bold hover:underline",
  // desktop too (owner, 2026-09-07) — sits below the back-to-top button and
  // shares its exact treatment (ring + shadow) so the two floaters read as
  // one family
  floating:
    "fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-full bg-violet-700 px-5 py-3 font-extrabold text-white shadow-[0_6px_20px_rgba(102,0,204,0.35)] ring-2 ring-white/80 transition-colors hover:bg-violet-800",
  plain: "inline-flex items-center gap-2 font-extrabold text-violet-700 hover:underline",
  // the artifact's .bookcard-tel — the number IS the headline of the sidebar
  // booking card, so it sets in ink at display size rather than as a link
  card: "inline-flex items-center gap-2 text-[1.25rem] font-extrabold tabular-nums text-ink transition-colors hover:text-violet-700",
};

export default function PhoneLink({
  variant = "primary",
  placement,
  display = PHONE_DISPLAY,
  tel = PHONE_TEL,
  showNumber = true,
}: PhoneLinkProps) {
  const { t } = useTranslation("main");

  const onClick = useCallback(() => {
    gtag.event("phone_click", {
      event_category: "conversion",
      event_label: placement ?? variant,
    });
  }, [placement, variant]);

  return (
    <a href={`tel:${tel}`} onClick={onClick} className={STYLES[variant]}>
      {ICON}
      <span>{showNumber ? display : t("cta.callUs")}</span>
    </a>
  );
}
